<template>
  <div class="container inner mx-auto px-4 py-8">
    <div class="lg:flex lg:space-x-8">
      <aside class="lg:w-1/4 mb-8 lg:mb-0">
        <!-- Панель фильтров -->
        <FilterPanel
          :availableLeagues="availableLeagues"
          @updateFilter="handleFilterUpdate"
        />
        <!-- Компонент с показателем прибыльности ставок -->
        <ProfitabilityPanel
          :results="results"
          :filter="filter"
          :selectedPeriod="selectedPeriod"
          :mapSportToGroup="mapSportToGroup"
          @update:selectedPeriod="val => (selectedPeriod = val)"
        />
      </aside>

      <main class="lg:w-3/4">
        <div v-if="loading" class="flex justify-center items-center h-64">
          <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <div v-else-if="error" class="text-red-500 text-center">
          {{ error }}
        </div>
        <TransitionGroup
          v-else
          name="match-list"
          tag="div"
          class="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          <MatchCard
            v-for="match in displayedResults"
            :key="match.id"
            :match="match"
          />
        </TransitionGroup>
        <div
          v-if="!loading && !error && displayedResults.length === 0"
          class="text-center text-gray-400 mt-8"
        >
          No results for selected filters.
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import FilterPanel from './FilterPanel.vue';
import MatchCard from './MatchCard.vue';
import ProfitabilityPanel from './ProfitabilityPanel.vue';

/*
  Функция нормализации спорта – например, "soccer_spain_la_liga" → "soccer".
  Она используется как для расчёта, так и для формирования списка лиг.
*/
function mapSportToGroup(sportKey) {
  if (!sportKey) return 'other';

  if (sportKey.startsWith('soccer_'))       return 'soccer';
  if (sportKey.startsWith('basketball_'))   return 'basketball';
  if (sportKey.startsWith('icehockey_'))    return 'icehockey';
  if (sportKey.startsWith('rugbyleague_'))  return 'rugbyleague';
  if (sportKey.startsWith('rugbyunion_'))   return 'rugbyunion';
  if (sportKey.startsWith('esports_'))      return 'esports';
  if (sportKey.startsWith('baseball_'))     return 'baseball';   // Все baseball_* → 'baseball'
  if (sportKey.startsWith('cricket_'))      return 'cricket';    // Все cricket_* → 'cricket'
  if (sportKey.startsWith('americanfootball')) return 'americanfootball';
  // Если ни одно условие не сработало, оставляем ключ “как есть”
  return sportKey;
}

/*
  Функция для красивого отображения названия лиги,
  например, "soccer_spain_la_liga" → "Spain La Liga"
*/
function beautifyLeagueTitle(leagueKey) {
  let text = leagueKey;
  const knownPrefixes = [
    'soccer_',
    'basketball_',
    'icehockey_',
    'rugbyleague_',
    'rugbyunion_',
    'esports_'
  ];
  for (const prefix of knownPrefixes) {
    if (text.startsWith(prefix)) {
      text = text.replace(prefix, '');
      break;
    }
  }
  text = text.replaceAll('_', ' ');
  text = text
    .split(' ')
    .map(word => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  if (text.toLowerCase() === 'epl') {
    text = 'EPL';
  }
  return text;
}

// Основные реактивные переменные
const results = ref([]);
const loading = ref(false);
const error = ref('');
// Фильтр: { sport, league }
const filter = ref({ sport: null, league: null });
// Количество отображаемых матчей (для подгрузки при скролле)
const displayCount = ref(20);
// Период расчёта прибыльности:
// 0 – за все время, 1 – сутки, 7 – неделя, 30 – месяц
const selectedPeriod = ref(7);

/*
  Функция загрузки данных.
  Важно: чтобы расчет "за все время" работал корректно,
  необходимо получить полный набор данных, а не только одну страницу (например, 25 записей).
  Если у вас есть эндпоинт, возвращающий все данные (например, /api/results/all или локальный results.json),
  измените URL ниже.
*/
async function fetchResults() {
  loading.value = true;
  error.value = '';
  try {
    // Пример: если у вас локальный файл, используйте 'results.json'
    // или другой эндпоинт, возвращающий полный массив ставок.
    const response = await fetch('/api/results');
    if (!response.ok) {
      throw new Error('Error fetching results from server');
    }
    const data = await response.json();

results.value = Array.isArray(data)
  ? data.filter(item => item.coefficient <= 12.2)
  : [];

  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

// Формируем список лиг на основе загруженных данных
const availableLeagues = computed(() => {
  const leaguesMap = new Map();
  results.value.forEach(match => {
    if (!match.league || !match.sport) return;
    const group = mapSportToGroup(match.sport);
    if (!leaguesMap.has(match.league)) {
      leaguesMap.set(match.league, {
        key: match.league,
        title: beautifyLeagueTitle(match.league),
        group
      });
    }
  });
  return Array.from(leaguesMap.values());
});

// Фильтрация матчей для отображения
const filteredResults = computed(() => {
  let temp = results.value;
  if (filter.value.league) {
    temp = temp.filter(m => m.league === filter.value.league);
  } else if (filter.value.sport) {
    temp = temp.filter(m => mapSportToGroup(m.sport) === filter.value.sport);
  }
  return temp;
});

// Сортировка по дате (свежие сверху)
const sortedResults = computed(() =>
  [...filteredResults.value].sort((a, b) => new Date(b.date) - new Date(a.date))
);

// Отображаем только первые displayCount матчей
const displayedResults = computed(() =>
  sortedResults.value.slice(0, displayCount.value)
);

function handleFilterUpdate(newFilter) {
  filter.value = newFilter;
  displayCount.value = 20;
}

function handleScroll() {
  const scrollThreshold = 300;
  if (
    window.innerHeight + window.scrollY >=
    document.body.offsetHeight - scrollThreshold
  ) {
    if (displayCount.value < sortedResults.value.length) {
      displayCount.value += 20;
    }
  }
}

onMounted(() => {
  fetchResults();
  window.addEventListener('scroll', handleScroll);
});
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.match-list-enter-active,
.match-list-leave-active {
  transition: all 0.5s ease;
}
.match-list-enter-from,
.match-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
